import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ICONS = {
  success: '✓',
  error:   '✕',
  info:    'ℹ',
  warning: '⚠',
};

const STYLES = {
  success: 'bg-[#1a4731] border-[#29752B] text-white',
  error:   'bg-[#5c1a1a] border-red-600 text-white',
  info:    'bg-[#1a2e4a] border-blue-500 text-white',
  warning: 'bg-[#4a3800] border-yellow-500 text-white',
};

const ICON_STYLES = {
  success: 'bg-[#29752B]',
  error:   'bg-red-600',
  info:    'bg-blue-500',
  warning: 'bg-yellow-500',
};

function ToastItem({ toast, onRemove }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const fadeTimer = setTimeout(() => setVisible(false), toast.duration - 400);
    const removeTimer = setTimeout(() => onRemove(toast.id), toast.duration);
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  }, [toast, onRemove]);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border shadow-lg max-w-sm w-full transition-all duration-400 ${STYLES[toast.type] || STYLES.info} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
      style={{ transform: visible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.3s ease, transform 0.3s ease' }}
    >
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold ${ICON_STYLES[toast.type]}`}>
        {ICONS[toast.type]}
      </div>
      <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onRemove(toast.id), 300); }}
        className="text-white/60 hover:text-white ml-1 text-lg leading-none flex-shrink-0"
      >×</button>
    </div>
  );
}

function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      setToasts((prev) => [
        ...prev,
        {
          id: e.detail.id || Date.now(),
          message: e.detail.message,
          type: e.detail.type || 'info',
          duration: e.detail.duration || 3500,
        },
      ]);
    };
    window.addEventListener('esave-toast', handler);
    return () => window.removeEventListener('esave-toast', handler);
  }, []);

  const remove = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  return createPortal(
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex flex-col gap-2 z-[99999] items-center px-4 w-full pointer-events-none"
      style={{ maxWidth: 420 }}
    >
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto w-full">
          <ToastItem toast={t} onRemove={remove} />
        </div>
      ))}
    </div>,
    document.body
  );
}

export default ToastContainer;
